import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
//eslint-disable-next-line
import { css } from "styled-components/macro";
import { motion } from "framer-motion";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { useTheme } from "../../contexts/ThemeContext.js";

import defaultCardImage from "images/shield-icon.svg";

import { ReactComponent as SvgDecoratorBlob3 } from "images/svg-decorator-blob-3.svg";

import SupportIconImage from "images/support-icon.svg";
import ShieldIconImage from "images/shield-icon.svg";
import CustomizeIconImage from "images/customize-icon.svg";
import FastIconImage from "images/fast-icon.svg";
import ReliableIconImage from "images/reliable-icon.svg";
import SimpleIconImage from "images/simple-icon.svg";

const Container = styled.div`
  ${tw`relative py-20 md:py-24`}
  background: var(--surface);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1), transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(139, 92, 246, 0.1), transparent 50%);
    z-index: 0;
  }
`;

const ThreeColumnContainer = styled.div`
  ${tw`flex flex-col items-center md:items-stretch md:flex-row flex-wrap md:justify-center max-w-screen-lg mx-auto relative z-10`}
`;
const Subheading = styled(motion(SubheadingBase))`
  ${tw`mb-4`}
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;
const Heading = styled(motion(SectionHeading))`
  ${tw`w-full`}
  color: var(--text);
  
  .highlight {
    background: linear-gradient(135deg, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;
const Description = styled(motion(SectionDescription))`
  ${tw`w-full text-center`}
  color: var(--text-secondary);
`;

const VerticalSpacer = tw.div`mt-16 w-full`

const Column = styled.div`
  ${tw`px-4 mb-8`}
  ${(props) =>
    props.$twoCols
      ? tw`md:w-1/2 max-w-md`
      : tw`md:w-1/2 lg:w-1/3 max-w-sm`}
`;

const Card = styled(motion.div)`
  ${tw`flex flex-col items-center text-center h-full p-8 rounded-2xl`}
  background: var(--glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
  box-shadow: 0 8px 32px var(--shadow);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent, rgba(59, 130, 246, 0.05));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 60px var(--shadow);
    border-color: var(--primary);
    
    &::before {
      opacity: 1;
    }
    
    .imageContainer {
      transform: scale(1.1) rotate(5deg);
      background: linear-gradient(135deg, var(--primary), var(--secondary));
      color: white;
    }
    
    .title {
      color: var(--primary);
      transform: translateY(-2px);
    }
  }
  
  .imageContainer {
    ${tw`border text-center rounded-2xl p-6 flex-shrink-0 mb-6 transition-all duration-300`}
    background: var(--glass);
    border-color: var(--glass-border);
    color: var(--primary);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 16px;
      padding: 2px;
      background: linear-gradient(135deg, var(--primary), var(--secondary));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    img {
      ${tw`w-8 h-8`}
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }

    svg {
      ${tw`w-8 h-8`}
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }
  }

  .textContainer {
    ${tw`flex flex-col flex-1`}
  }

  .title {
    ${tw`tracking-wide font-bold text-xl leading-none mb-4 transition-all duration-300`}
    color: var(--text);
  }

  .description {
    ${tw`font-medium leading-relaxed transition-all duration-300`}
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const DecoratorBlob = styled(SvgDecoratorBlob3)`
  ${tw`pointer-events-none absolute right-0 bottom-0 w-64 opacity-25 transform translate-x-32 translate-y-48`}
  fill: url(#gradient2);
  animation: float 8s ease-in-out infinite;
  filter: blur(1px);
`;

export default ({ cards = null, columns = 3, heading = "Fonctionnalités <span class='highlight'>Exceptionnelles</span>", subheading = "Caractéristiques", description = "Nous sommes déterminés à simplifier et optimiser la gestion de vos visiteurs. Notre système innovant est conçu pour vous offrir un service exceptionnel à chaque étape de l'accueil et de l'enregistrement de vos invités. Voici pourquoi choisir Tanga" }) => {
  const { isDarkMode } = useTheme();
  const twoCols = columns === 2;
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const defaultCards = [
    {
      imageSrc: ShieldIconImage,
      title: "Sécurisé",
      description: "Nous travaillons uniquement avec des fournisseurs offrant un niveau de sécurité optimal et des protocoles de chiffrement avancés."
    },
    {
      imageSrc: SupportIconImage,
      title: "Support 24/7",
      description: "Notre équipe d'assistance est disponible 24 heures sur 24, 7 jours sur 7 pour répondre à toutes vos questions."
    },
    {
      imageSrc: CustomizeIconImage,
      title: "Personnalisable",
      description: "Adaptez Tanga à vos besoins spécifiques grâce à nos options de personnalisation avancées."
    },
    {
      imageSrc: ReliableIconImage,
      title: "Fiable",
      description: "Faites confiance à Tanga pour une gestion des visiteurs fiable et efficace avec une disponibilité de 99.9%."
    },
    {
      imageSrc: FastIconImage,
      title: "Rapide",
      description: "Tanga offre un processus d'enregistrement rapide et intuitif pour vos visiteurs en moins de 30 secondes."
    },
    {
      imageSrc: SimpleIconImage,
      title: "Simple",
      description: "Tanga est conçu pour être facile à utiliser, même pour les utilisateurs non techniques."
    }
  ];

  if (!cards) cards = defaultCards;

  return (
    <Container>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={containerVariants}
      >
        <ThreeColumnContainer>
          {subheading && <Subheading variants={itemVariants}>{subheading}</Subheading>}
          <Heading variants={itemVariants}>{heading}</Heading>
          {description && <Description variants={itemVariants}>{description}</Description>}
          <VerticalSpacer />
          {cards.map((card, i) => (
            <Column key={i} $twoCols={twoCols}>
              <Card
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <span className="imageContainer">
                  {card.Icon ? (
                    <card.Icon aria-hidden="true" />
                  ) : (
                    <img src={card.imageSrc || defaultCardImage} alt={card.title || "Icon"} />
                  )}
                </span>
                <span className="textContainer">
                  <span className="title">{card.title || "Fully Secure"}</span>
                  <p className="description">
                    {card.description || "Lorem ipsum donor amet siti ceali ut enim ad minim veniam, quis nostrud."}
                  </p>
                </span>
              </Card>
            </Column>
          ))}
        </ThreeColumnContainer>
      </motion.div>
      
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={isDarkMode ? "#3b82f6" : "#2563eb"} stopOpacity="0.8" />
            <stop offset="100%" stopColor={isDarkMode ? "#8b5cf6" : "#7c3aed"} stopOpacity="0.8" />
          </linearGradient>
        </defs>
      </svg>
      
      <DecoratorBlob />
    </Container>
  );
};
